import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './GetStarted.css';

function GetStarted() {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Contact & Business Info
    name: '',
    email: '',
    phone: '',
    businessName: '',
    industry: '',
    websiteUrl: '',
    // Step 2 - Assets & Resources
    contentProvision: '',
    productImages: '',
    logoAndBranding: '',
    domainStatus: '',
    domainName: '',
    referenceStores: '',
    // Step 3 - Store Preferences
    designStyle: '',
    budget: '',
    timeline: '',
    productCount: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const totalSteps = 3;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Pre-fill form data from URL parameters
  useEffect(() => {
    const name = searchParams.get('name');
    const email = searchParams.get('email');
    const phone = searchParams.get('phone');
    const business = searchParams.get('business');
    const category = searchParams.get('category');

    if (name || email || phone || business || category) {
      setFormData(prev => ({
        ...prev,
        name: name || prev.name,
        email: email || prev.email,
        phone: phone || prev.phone,
        businessName: business || prev.businessName,
        industry: category || prev.industry
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getRequiredFieldsForStep = (step) => {
    switch (step) {
      case 1:
        return ['name', 'email', 'phone', 'businessName'];
      case 2:
        return ['contentProvision', 'productImages', 'logoAndBranding', 'domainStatus'];
      case 3:
        return ['designStyle', 'budget', 'timeline', 'productCount'];
      default:
        return [];
    }
  };

  const validateStep = (step) => {
    const requiredFields = getRequiredFieldsForStep(step);
    return requiredFields.every(field => formData[field].trim() !== '');
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      if (!validateStep(currentStep)) {
        // Trigger native validation by finding the first empty required field
        const requiredFields = getRequiredFieldsForStep(currentStep);
        for (const field of requiredFields) {
          if (!formData[field].trim()) {
            const el = document.getElementById(field === 'domainStatus' ? 'domainStatus' : field);
            if (el && el.reportValidity) {
              el.reportValidity();
            }
            return;
          }
        }
        return;
      }
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
      } else {
        setSubmitError('Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const industryOptions = [
    { value: '', label: 'Select your industry' },
    { value: 'accountant-tax', label: 'Accountant / Tax Services' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'attorney-law', label: 'Attorney / Law Firm' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'bar-restaurant', label: 'Bar / Restaurant' },
    { value: 'beauty', label: 'Beauty & Cosmetics' },
    { value: 'construction', label: 'Construction' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'dentist', label: 'Dentist' },
    { value: 'doctors-health', label: 'Doctors & Other Health Professionals' },
    { value: 'ecommerce', label: 'E-Commerce (Online Retail)' },
    { value: 'electronics', label: 'Electronics & Tech' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'fashion', label: 'Fashion & Apparel' },
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'food-beverage', label: 'Food & Beverage' },
    { value: 'general-contractor', label: 'General Contractor' },
    { value: 'health', label: 'Health & Wellness' },
    { value: 'home', label: 'Home & Living' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'internet-web', label: 'Internet / Web-Based Services' },
    { value: 'jewelry', label: 'Jewelry & Accessories' },
    { value: 'private-equity', label: 'Private Equity & Investment Firms' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'retail', label: 'Retail (Physical Storefront)' },
    { value: 'sports', label: 'Sports & Outdoors' },
    { value: 'technology', label: 'Technology' },
    { value: 'toys', label: 'Toys & Games' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'trucking', label: 'Trucking' },
    { value: 'other', label: 'Other' }
  ];

  const designStyleOptions = [
    { value: '', label: 'Select preferred style' },
    { value: 'modern', label: 'Modern & Minimalist' },
    { value: 'bold', label: 'Bold & Vibrant' },
    { value: 'luxury', label: 'Luxury & Premium' },
    { value: 'playful', label: 'Playful & Fun' },
    { value: 'classic', label: 'Classic & Traditional' },
    { value: 'unsure', label: 'Not Sure Yet' }
  ];

  const budgetOptions = [
    { value: '', label: 'Select your budget' },
    { value: 'under-1k', label: 'Under $1,000' },
    { value: '1k-3k', label: '$1,000 - $3,000' },
    { value: '3k-5k', label: '$3,000 - $5,000' },
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-20k', label: '$10,000 - $20,000' },
    { value: '20k-plus', label: '$20,000+' }
  ];

  const timelineOptions = [
    { value: '', label: 'Select timeline' },
    { value: 'asap', label: 'ASAP (Within 2 weeks)' },
    { value: '1-3-months', label: '1-3 months' },
    { value: '3-6-months', label: '3-6 months' },
    { value: '6-plus-months', label: '6+ months' },
    { value: 'exploring', label: 'Just exploring' }
  ];

  const productCountOptions = [
    { value: '', label: 'Select product count' },
    { value: '1-50', label: '1-50 products' },
    { value: '51-200', label: '51-200 products' },
    { value: '201-500', label: '201-500 products' },
    { value: '500-plus', label: '500+ products' }
  ];

  const contentProvisionOptions = [
    { value: '', label: 'Select an option' },
    { value: 'provide-all', label: "I'll provide all content" },
    { value: 'need-help', label: 'I need help creating content' }
  ];

  const productImagesOptions = [
    { value: '', label: 'Select an option' },
    { value: 'provide-all', label: "I'll provide all product images" },
    { value: 'need-help', label: 'I need help with product images' }
  ];

  const logoAndBrandingOptions = [
    { value: '', label: 'Select an option' },
    { value: 'logo-only', label: 'I have a logo but need brand guidelines' },
    { value: 'need-logo', label: 'I need a logo designed' }
  ];

  const domainStatusOptions = [
    { value: '', label: 'Select an option' },
    { value: 'have-domain', label: 'I already own a domain' },
    { value: 'need-purchase', label: 'I need to purchase a domain' }
  ];

  const stepTitles = {
    1: { title: 'Contact & Business Information', subtitle: 'Let us know how to reach you and about your business' },
    2: { title: 'Store Branding', subtitle: 'What will you provide vs. what we create' },
    3: { title: 'Store Preferences', subtitle: 'Help us understand your needs' }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={`step-content ${isAnimating ? 'animating' : ''}`}>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'name' ? 'focused' : ''} ${formData.name ? 'filled' : ''}`}>
                <label htmlFor="name">Full Name *</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="John Doe"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'filled' : ''}`}>
                <label htmlFor="email">Email Address *</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="john@company.com"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'phone' ? 'focused' : ''} ${formData.phone ? 'filled' : ''}`}>
                <label htmlFor="phone">Phone Number *</label>
                <div className="input-wrapper">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="+1 (555) 123-4567"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'businessName' ? 'focused' : ''} ${formData.businessName ? 'filled' : ''}`}>
                <label htmlFor="businessName">Business / Company Name *</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('businessName')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="Your Company Name"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'industry' ? 'focused' : ''} ${formData.industry ? 'filled' : ''}`}>
                <label htmlFor="industry">Industry <span className="optional-tag">(Optional)</span></label>
                <div className="input-wrapper">
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('industry')}
                    onBlur={() => setFocusedField(null)}
                  >
                    {industryOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'websiteUrl' ? 'focused' : ''} ${formData.websiteUrl ? 'filled' : ''}`}>
                <label htmlFor="websiteUrl">Website URL <span className="optional-tag">(Optional)</span></label>
                <div className="input-wrapper">
                  <input
                    type="url"
                    id="websiteUrl"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('websiteUrl')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="https://yourwebsite.com"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={`step-content ${isAnimating ? 'animating' : ''}`}>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'contentProvision' ? 'focused' : ''} ${formData.contentProvision ? 'filled' : ''}`}>
                <label htmlFor="contentProvision">Store Content (About, Policies, Descriptions) *</label>
                <div className="input-wrapper">
                  <select
                    id="contentProvision"
                    name="contentProvision"
                    value={formData.contentProvision}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('contentProvision')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {contentProvisionOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'productImages' ? 'focused' : ''} ${formData.productImages ? 'filled' : ''}`}>
                <label htmlFor="productImages">Product Images *</label>
                <div className="input-wrapper">
                  <select
                    id="productImages"
                    name="productImages"
                    value={formData.productImages}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('productImages')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {productImagesOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'logoAndBranding' ? 'focused' : ''} ${formData.logoAndBranding ? 'filled' : ''}`}>
                <label htmlFor="logoAndBranding">Logo & Branding *</label>
                <div className="input-wrapper">
                  <select
                    id="logoAndBranding"
                    name="logoAndBranding"
                    value={formData.logoAndBranding}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('logoAndBranding')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {logoAndBrandingOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'domainStatus' ? 'focused' : ''} ${formData.domainStatus ? 'filled' : ''}`}>
                <label htmlFor="domainStatus">Domain Name *</label>
                <div className="input-wrapper">
                  <select
                    id="domainStatus"
                    name="domainStatus"
                    value={formData.domainStatus}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('domainStatus')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {domainStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            {formData.domainStatus === 'have-domain' && (
              <div className={`form-group conditional-field ${focusedField === 'domainName' ? 'focused' : ''} ${formData.domainName ? 'filled' : ''}`}>
                <label htmlFor="domainName">Your Domain Name</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="domainName"
                    name="domainName"
                    value={formData.domainName}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('domainName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="e.g., yourbrand.com"
                  />
                  <div className="input-border"></div>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className={`step-content ${isAnimating ? 'animating' : ''}`}>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'designStyle' ? 'focused' : ''} ${formData.designStyle ? 'filled' : ''}`}>
                <label htmlFor="designStyle">Preferred Store Design/Style *</label>
                <div className="input-wrapper">
                  <select
                    id="designStyle"
                    name="designStyle"
                    value={formData.designStyle}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('designStyle')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {designStyleOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'budget' ? 'focused' : ''} ${formData.budget ? 'filled' : ''}`}>
                <label htmlFor="budget">Budget Range *</label>
                <div className="input-wrapper">
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('budget')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {budgetOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${focusedField === 'timeline' ? 'focused' : ''} ${formData.timeline ? 'filled' : ''}`}>
                <label htmlFor="timeline">Timeline / Urgency *</label>
                <div className="input-wrapper">
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('timeline')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {timelineOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
              <div className={`form-group ${focusedField === 'productCount' ? 'focused' : ''} ${formData.productCount ? 'filled' : ''}`}>
                <label htmlFor="productCount">Estimated Product Count *</label>
                <div className="input-wrapper">
                  <select
                    id="productCount"
                    name="productCount"
                    value={formData.productCount}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('productCount')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    {productCountOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <div className="input-border"></div>
                </div>
              </div>
            </div>
            <div className={`form-group ${focusedField === 'referenceStores' ? 'focused' : ''} ${formData.referenceStores ? 'filled' : ''}`}>
              <label htmlFor="referenceStores">Reference / Inspiration Stores <span className="optional-tag">(Optional)</span></label>
              <div className="input-wrapper">
                <textarea
                  id="referenceStores"
                  name="referenceStores"
                  value={formData.referenceStores}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('referenceStores')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Share URLs of stores you admire or want your store to look like..."
                  rows={3}
                />
                <div className="input-border"></div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="get-started-section">
      <div className="get-started-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="container">
        <div className="get-started-header">
          <h1 className="get-started-title">
            <span className="title-highlight">Start Your Project</span>
          </h1>
        </div>

        <div className="form-container">
          {/* Progress Indicator */}
          <div className="progress-wrapper">
            <div className="step-indicators">
              {[1, 2, 3].map(step => (
                <div
                  key={step}
                  className={`step-indicator ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                >
                  <span className="step-label">Step-{step}</span>
                </div>
              ))}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Form Wrapper */}
          <div className="form-wrapper">
            <div className="form-background">
              <div className="form-glow"></div>
              <div className="form-particles">
                <span className="form-particle"></span>
                <span className="form-particle"></span>
                <span className="form-particle"></span>
              </div>
            </div>

            {submitted ? (
              <div className="success-message">
                <div className="success-icon-wrapper">
                  <div className="success-icon">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="#10B981"/>
                      <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="success-rings">
                    <div className="success-ring"></div>
                    <div className="success-ring"></div>
                    <div className="success-ring"></div>
                  </div>
                </div>
                <h3>Thank You!</h3>
                <p>We've received your information and will get back to you within 24 hours.</p>
                <button
                  className="btn-primary btn-large"
                  onClick={() => window.location.href = '/'}
                >
                  <span className="btn-text">Back to Home</span>
                  <span className="btn-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="multi-step-form">
                {/* Step Header */}
                <div className="step-header">
                  <h2>{stepTitles[currentStep].title}</h2>
                  <p>{stepTitles[currentStep].subtitle}</p>
                </div>

                {/* Step Content */}
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      className="btn-secondary btn-back"
                      onClick={handleBack}
                    >
                      <span className="btn-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className="btn-text">Back</span>
                    </button>
                  )}

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      className="btn-primary btn-next"
                      onClick={handleNext}
                    >
                      <span className="btn-text">Next</span>
                      <span className="btn-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <button type="submit" className="btn-primary btn-submit" disabled={isSubmitting}>
                      <span className="btn-text">{isSubmitting ? 'Submitting...' : 'Submit'}</span>
                      {!isSubmitting && (
                        <span className="btn-icon">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M18.3333 1.66666L9.16666 10.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.3333 1.66666L12.5 18.3333L9.16666 10.8333L1.66666 7.49999L18.3333 1.66666Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      )}
                    </button>
                  )}
                </div>

                {submitError && (
                  <div className="submit-error">
                    <span>{submitError}</span>
                  </div>
                )}
              </form>
            )}

            {/* Trust Indicators */}
            {!submitted && (
              <div className="trust-indicators">
                <div className="trust-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 1.66666L2.5 5.00001V9.16668C2.5 13.4167 5.75 17.4167 10 18.3333C14.25 17.4167 17.5 13.4167 17.5 9.16668V5.00001L10 1.66666Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.5 10L9.16667 11.6667L12.5 8.33334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>SSL Secured</span>
                </div>
                <div className="trust-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39762 14.6024 1.66666 10 1.66666C5.39763 1.66666 1.66667 5.39762 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7.5 10L9.16667 11.6667L12.5 8.33334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Shopify Partner</span>
                </div>
                <div className="trust-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.6667 3.33334H3.33333C2.41286 3.33334 1.66667 4.07953 1.66667 5.00001V15C1.66667 15.9205 2.41286 16.6667 3.33333 16.6667H16.6667C17.5872 16.6667 18.3333 15.9205 18.3333 15V5.00001C18.3333 4.07953 17.5872 3.33334 16.6667 3.33334Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M1.66667 8.33334H18.3333" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <span>No Spam</span>
                </div>
                <div className="trust-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39762 14.6024 1.66666 10 1.66666C5.39763 1.66666 1.66667 5.39762 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 5V10L13.3333 11.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>24/7 Support</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default GetStarted;
